import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import { Box } from '@material-ui/core';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import Step2 from 'components/Forms/ExpeditionCreateForm/Step2';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { Expedition, ExpeditionForm as ExpeditionFormType, SubmissionError } from 'types';
import { formatExpeditionBeforePut, formatExpeditionForEdit } from 'utils/normalizer';
import { useExpeditionById, useSortingCenterContractStatusesIndexedByClientIds } from 'hooks';
import PageContent from 'components/Generic/PageContent';
import RequiredFieldsLegend from 'components/Forms/RequiredFieldsLegend';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import Loading from 'components/Loading';
import { putExpedition } from 'services/apiOperations';
import { goBack } from 'utils/history';
import { generateNotifyFromViolations } from 'services/errors';
import { transformId } from 'services/apiPlatformTransformer';

const ExpeditionEdit: React.FC = () => {
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { expeditionId } = useParams<{ expeditionId: string }>();
  const location = useLocation<{ referer?: string }>();
  const backRoute = location.state?.referer ?? generatePath(Routes.ExpeditionShow, { expeditionId });
  const { loading, value: expedition } = useExpeditionById(expeditionId);
  const originalExpeditionClients = expedition?.expeditionClientPortions.map(
    expeditionClientPortion => expeditionClientPortion.client,
  );
  const buyer = expedition?.buyer;
  const material = expedition?.material;

  const getExpeditionDate = (expedition: Expedition | null | undefined): string => {
    if (!expedition) {
      return new Date().toISOString();
    }

    let expeditionDateCurrentTZ = '';
    if (expedition.expeditionDate) {
      const dateParts = expedition.expeditionDate.split('+');
      if (dateParts.length > 0) {
        expeditionDateCurrentTZ = dateParts[0];
      }
    }

    let expeditionDate = new Date();

    if (expeditionDateCurrentTZ) {
      expeditionDate = new Date(expeditionDateCurrentTZ);
    }

    return expeditionDate.toISOString();
  };

  const sortingCenterContractStatusesIndexedByClientId = useSortingCenterContractStatusesIndexedByClientIds(
    transformId(expedition?.material['@id']),
    transformId(expedition?.buyer['@id']),
    getExpeditionDate(expedition),
  );

  const onSubmit = async (expedition: ExpeditionFormType) => {
    if (isSubmitting || !sortingCenter) {
      return;
    }
    const formattedExpedition = formatExpeditionBeforePut(expedition, sortingCenter['@id']);
    setIsSubmitting(true);
    putExpedition(formattedExpedition, expeditionId)
      .then(() => {
        notify({
          message: t('expeditionCreateForm.edition.success'),
          severity: 'success',
        });
        goBack(backRoute, history);
      })
      .catch((e: SubmissionError) => {
        if (e.status === 412 && e.response.body.violations) {
          generateNotifyFromViolations(e.response.body.violations, t);
        } else {
          notify({
            message: t('errorGeneric'),
            severity: 'error',
          });
        }
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <PageContent title={t('expeditions.edit.title')} withButtonBar noPadding>
        <Box py={4}>
          {loading ? (
            <Loading />
          ) : (
            expedition &&
            sortingCenterContractStatusesIndexedByClientId && (
              <Step2
                onSubmit={onSubmit}
                previousStepData={formatExpeditionForEdit(expedition, sortingCenterContractStatusesIndexedByClientId)}
                formName="expeditions_form"
                setFormContext={() => {}}
                isUpdateMode={true}
                originalExpeditionClients={originalExpeditionClients}
                buyerForUpdate={buyer}
                materialForUpdate={material}
              />
            )
          )}
        </Box>
        <RequiredFieldsLegend />
      </PageContent>
      <BottomButtonBar
        formName="expeditions_form"
        previousStepLabel={t('expeditions.edit.cancelButtonLabel')}
        onClickPrevious={() => goBack(backRoute, history)}
        disableNext={isSubmitting}
        nextStepIcon={<CheckCircleOutlineOutlinedIcon />}
        nextStepLabel={t('expeditions.edit.editButtonLabel')}
      />
    </>
  );
};
export default ExpeditionEdit;
